import React, { useEffect, useRef, useState } from 'react';
import './Footer.scss';
import { useTranslation } from 'react-i18next';
import FooterLinksComponent from '../FooterLinksComponent/FooterLinksComponent.tsx';
import { FooterLinksComponentProps } from '../../constants/interface';
import { useCustomNavigation } from '../../utility/Utility.ts';

const Footer = () => {
    const {t} = useTranslation();
    const customNavigate = useCustomNavigation();
    const footerSecondChildRef = useRef<HTMLDivElement | null>(null);
    const [footerSecondChildHeight, setFooterSecondChildHeight] = useState(0);
    const videoUrl = '/assets/videos/mp4/footer-video-1.mp4';


    useEffect(() => {
        if (footerSecondChildRef.current) {
            setFooterSecondChildHeight(footerSecondChildRef?.current.offsetHeight); // Get the height of the div
        }
    
        const handleResize = () => {
            if (footerSecondChildRef.current) {
                setFooterSecondChildHeight(footerSecondChildRef?.current.offsetHeight); // Update on window resize
            }
        };
    
        // Listen for window resize
        window.addEventListener("resize", handleResize);
    
        // Cleanup event listener on component unmount
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <footer id="footer">
            <div className="section video-container">
                <video autoPlay loop muted playsInline className="video-background bg-blue-132">
                    <source src={videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="content px-5 md:max-w-[40rem] lg:px-0">
                    <div className="grid gap-11 lg:gap-10">
                        <div className="grid gap-4 lg:gap-6">
                            <p className="text-3xl font-bold sm:w-[30rem] m-auto lg:text-5xl">{t('FOOTER.ENJOY_RELIABLE_TELECOM_SOLUTIONS')}</p>
                            <p className="text-sm font-medium text-cyan-E9F lg:text-base">{t('FOOTER.OUR_SOLUTIONS_ELEVATE_OPERATIONS')}</p>
                        </div>
                        <div className="btn-container justify-center">
                            <button className="btn bg-white text-gray-4D4 w-full justify-center sm:w-fit" onClick={() => customNavigate(true, 'services/digital-transformation')}>
                                {t('NAVBAR.LEARN_MORE')}
                                <img loading="lazy" src="/assets/images/svg/right-arrow.svg" alt="->" />
                            </button>
                            <button className="btn bg-transparent text-white border w-full justify-center sm:w-fit" onClick={() => customNavigate(true, 'contact-us')}>
                                {t('FOOTER.CONTACT_SALES')}
                                <img loading="lazy" src="/assets/images/svg/link-external-icon.svg" alt="-" />
                            </button>
                        </div>
                    </div>
                </div>
                {/* The below div is used to add dynamic space, so that the content in the parent does not get hidden under the second floating div. */}
                <div style={{ height: `${footerSecondChildHeight / 2}px` }}></div>
            </div>
            <div ref={footerSecondChildRef} className="bg-blue-EEF p-6 grid gap-8 rounded-md sm:rounded-[1.25rem] xl:p-14 xl:w-[80rem]">
                <p className="text-base font-medium text-gray-666 text-center lg:text-lg">{t('FOOTER.OUR_PARTNERS')}</p>
                <div className="flex flex-wrap gap-8 justify-center rounded-[1.25rem]">
                    {
                        partnerList.map((item: any, index: number) => (
                            <img loading="lazy" key={index} src={item?.img || ''} alt={t(item?.name)} title={t(item?.name)}
                                className="cursor-default h-7 md:h-auto"
                            />
                        ))
                    }
                </div>
            </div>
            <div className="section bg-blue-1F3 text-white" style={{paddingBottom: 0}}>
                {/* The below div is used to add dynamic space, so that the content in the parent does not get hidden under the second floating div. */}
                <div style={{ height: `${footerSecondChildHeight / 2}px` }}></div>
                <div className="max-w-[80rem] p-5 m-auto grid gap-16 xl:p-0">
                    <div className="flex flex-wrap gap-11 justify-between">
                        <div className="grid grid-rows-[auto,1fr] gap-4 max-w-64">
                            <img loading="lazy" src="/assets/images/png/logo-2.webp" alt="" />
                            <p className="text-xs font-normal">{t('FOOTER.INNOVII_SERVICE_PROVIDER')}</p>
                        </div>
                        <div className="flex flex-wrap gap-20">
                            {
                                FooterLinkList.map((item: FooterLinksComponentProps, index: number) => (
                                    <div key={index}>
                                        <FooterLinksComponent
                                            title={item.title}
                                            links={item.links}
                                        />
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    <div className="flex flex-wrap-reverse gap-10 justify-between">
                        <div>
                            <p className="text-sm font-normal flex gap-2">
                                <span>{t('FOOTER.COPYRIGHT')}</span>
                                <span>{t('FOOTER.ALL_RIGHTS_RESERVED')}</span>
                            </p>
                        </div>
                        {/* <div className="flex flex-wrap gap-4">
                            {
                                FooterSocialMediaLinkList.map((item: any, index: number) => (
                                    <div key={index} className="flex gap-2 items-center cursor-pointer" onClick={() => window.open(item?.link, '_blank')}>
                                        <img loading="lazy" src={item?.img} alt={t(item?.title)} />
                                        <p className="text-sm font-medium">{t(item?.title)}</p>
                                    </div>
                                ))
                            }
                        </div> */}
                    </div>
                </div>
                <img loading="lazy" src="/assets/images/png/INNOVII.webp" alt="" className="m-auto pt-20" />
            </div>
        </footer>
    );
};

export default Footer;

const partnerList: any[] = [
    {
        name: "HOME_PAGE.ETHIO_TELECOM",
        img: "/assets/images/png/partner-logo-1.webp",
    },
    {
        name: "HOME_PAGE.HORMUUD_TELECOM",
        img: "/assets/images/png/partner-logo-2.webp",
    },
    {
        name: "HOME_PAGE.SAFARICOM",
        img: "/assets/images/png/partner-logo-3.webp",
    },
    {
        name: "HOME_PAGE.TELESOM",
        img: "/assets/images/png/partner-logo-4.webp",
    },
    {
        name: "HOME_PAGE.GOLIS",
        img: "/assets/images/png/partner-logo-5.webp",
    },
    {
        name: "HOME_PAGE.MTN",
        img: "/assets/images/svg/partner-logo-6.svg",
    },
];

const FooterLinkList: FooterLinksComponentProps[] = [
    {
        title: 'FOOTER.COMPANY',
        links: [
            {
                isRouterLink: true,
                img: '',
                title: 'NAVBAR.ABOUT_US',
                link: 'about-us',
            },
            {
                isRouterLink: true,
                img: '',
                title: 'FOOTER.PRODUCTS',
                link: 'products/m-woman',
            },
            {
                isRouterLink: true,
                img: '',
                title: 'FOOTER.SERVICES',
                link: 'services/sdf',
            },
            {
                isRouterLink: true,
                img: '',
                title: 'FOOTER.CONTACT_US',
                link: 'contact-us',
            },
        ],
    },
    // {
    //     title: 'FOOTER.LEGAL',
    //     links: [
    //         {
    //             img: '',
    //             title: 'FOOTER.PRIVACY_POLICY',
    //             link: '',
    //         },
    //         {
    //             img: '',
    //             title: 'FOOTER.TERMS_AND_CONDITIONS',
    //             link: '',
    //         },
    //     ],
    // },
    // {
    //     title: 'FOOTER.COMPANY',
    //     links: [
    //         {
    //             isRouterLink: true,
    //             img: '',
    //             title: 'NAVBAR.ABOUT_US',
    //             link: 'about-us',
    //         },
    //         {
    //             isRouterLink: true,
    //             img: '',
    //             title: 'FOOTER.CAREERS',
    //             link: '',
    //         },
    //         {
    //             img: '',
    //             title: 'FOOTER.PRICING',
    //             link: '',
    //         },
    //         {
    //             img: '',
    //             title: 'FOOTER.FAQS',
    //             link: '',
    //         },
    //     ],
    // },
    {
        title: 'FOOTER.ADDRESS',
        links: [
            {
                img: '/assets/images/svg/location-icon-1.svg',
                title: 'FOOTER.AJMAN_MEDIA_CITY_ADDRESS',
                link: '',
            },
            {
                img: '/assets/images/svg/phone-icon-1.svg',
                title: 'FOOTER.PHONE_NUMBER',
                link: '',
            },
            {
                img: '/assets/images/svg/email-icon-1.svg',
                title: 'FOOTER.EMAIL_INNOVII',
                link: '',
            },
            {
                isRouterLink: false,
                img: '/assets/images/svg/linkedin-icon.svg',
                title: 'COMMON.LINKEDIN',
                link: 'https://www.linkedin.com/company/innovii-digital-services/?lipi=urn%3Ali%3Apage%3Ad_flagship3_search_srp_all%3BT9PjXTQ4Ra%2BGsb3yWq3Agw%3D%3D',
            },
        ],
    },
];

const FooterSocialMediaLinkList: any[] = [
    {
        img: '/assets/images/svg/linkedin-icon.svg',
        title: 'COMMON.LINKEDIN',
        link: 'https://www.linkedin.com/company/innovii-digital-services/?lipi=urn%3Ali%3Apage%3Ad_flagship3_search_srp_all%3BT9PjXTQ4Ra%2BGsb3yWq3Agw%3D%3D',
    },
    {
        img: '/assets/images/svg/x-icon.svg',
        title: 'COMMON.X_FORMERLY_TWITTER',
        link: 'https://twitter.com',
    },
    {
        img: '/assets/images/svg/instagram-icon.svg',
        title: 'COMMON.INSTAGRAM',
        link: 'https://www.instagram.com',
    },
    {
        img: '/assets/images/svg/facebook-icon.svg',
        title: 'COMMON.FACEBOOK',
        link: 'https://www.facebook.com',
    },
    {
        img: '/assets/images/svg/youtube-icon.svg',
        title: 'COMMON.YOUTUBE',
        link: 'https://www.youtube.com',
    },
];